import { useState } from "react";
import { Share2, Copy, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable share button.
 * - On mobile & modern browsers → opens the native OS share sheet (WhatsApp, Instagram, Telegram, e-mail, etc.)
 * - Fallback → shows a small popover with "WhatsApp" and "Copiar link" buttons
 *
 * Props:
 *  - title:    Sharing title
 *  - text:     Descriptive text to include
 *  - url:      URL to share (default: current window location)
 *  - variant:  'primary' | 'outline' | 'ghost' | 'icon'
 *  - label:    Text shown next to the icon (hidden when variant='icon')
 */
export default function ShareButton({
  title = "CarnesWork — Diárias de Açougue",
  text = "Encontre açougueiros e balconistas na hora — ou pegue diárias e receba no dia. Baixe o CarnesWork:",
  url,
  variant = "outline",
  label = "Compartilhar",
  testid = "btn-share",
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? `${window.location.origin}${window.location.pathname === "/" ? "" : window.location.pathname}` : "https://diacougue.com.br");
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${text}\n\n${shareUrl}`)}`;

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return true;
      } catch (e) {
        // user cancelled or share API not permitted — fall through to fallback
        if (e && e.name !== "AbortError") console.warn("[share] native share failed:", e);
      }
    }
    return false;
  };

  const handleClick = async (e) => {
    e?.stopPropagation?.();
    const ok = await nativeShare();
    if (!ok) setOpen((v) => !v);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 1500);
      setOpen(false);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const shareWhatsapp = () => {
    setOpen(false);
    // handled by the <a href> — no need to trigger anything
  };

  const baseClasses =
    variant === "primary" ? "cw-btn-primary text-sm inline-flex items-center gap-2" :
    variant === "ghost"   ? "text-sm text-zinc-300 hover:text-white inline-flex items-center gap-2 px-3 py-2" :
    variant === "icon"    ? "w-9 h-9 rounded-lg border border-zinc-800 hover:border-red-600 flex items-center justify-center text-zinc-300 hover:text-white transition-colors" :
                            "cw-btn-outline text-sm inline-flex items-center gap-2";

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={handleClick}
        className={baseClasses}
        data-testid={testid}
        title="Compartilhar"
      >
        <Share2 className="w-4 h-4" />
        {variant !== "icon" && <span>{label}</span>}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            data-testid="share-backdrop"
          />
          <div
            className="absolute right-0 mt-2 w-56 rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl overflow-hidden z-50 anim-fade-up"
            data-testid="share-popover"
          >
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={shareWhatsapp}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-zinc-900 transition-colors"
              data-testid="share-whatsapp"
            >
              <MessageCircle className="w-4 h-4 text-emerald-400" />
              WhatsApp
            </a>
            <button
              onClick={copyLink}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-zinc-900 transition-colors border-t border-zinc-800"
              data-testid="share-copy"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-zinc-400" />}
              {copied ? "Copiado!" : "Copiar link"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
